import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LoginService } from '../login.service';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { LoadingService } from '../../../auxiliares/servicios/loading.service';
import { Router } from '@angular/router';
import { EnvService } from '../../../env';
import { TemaRuntimeService } from '../../../auxiliares/servicios/tema-runtime.service';
import { Observable } from 'rxjs';
import { ITema } from 'modelos/src';
import { GOOGLE_CLIENT_ID } from '../../../../environments/environment';

declare const google: any;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: false,
})
export class LoginComponent implements OnInit, AfterViewInit {
  private login = inject(LoginService);
  helper = inject(HelperService);
  loading = inject(LoadingService);
  private router = inject(Router);
  envService = inject(EnvService);
  private temaRuntime = inject(TemaRuntimeService);

  @ViewChild('googleBtn') googleBtn?: ElementRef<HTMLElement>;
  public readonly googleHabilitado = !!GOOGLE_CLIENT_ID;

  public inputType = 'password';
  public form?: FormGroup;
  public tema$: Observable<ITema | null>;

  constructor() {
    this.tema$ = this.temaRuntime.tema$;
  }

  private buildForm() {
    this.form = new FormGroup({
      usuario: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required]),
    });
  }

  // on

  public onClickShowPassword() {
    this.inputType = this.inputType === 'password' ? 'text' : 'password';
  }

  public goToForgotPassword() {
    this.router.navigateByUrl('/login/forgot-password');
  }

  public goToRegistro() {
    this.router.navigateByUrl('/login/registro');
  }

  public async onSubmit() {
    // Ingresar queda habilitado (deshabilitado se veía como un botón roto):
    // con un campo vacío, se marcan los campos y no se envía.
    if (this.form?.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading.setLoading(true);
    try {
      const user = this.form?.get('usuario')?.value;
      const pass = this.form?.get('password')?.value;
      await this.login.login(user, pass);
      // Realizar navegación automática basada en módulos habilitados
      this.helper.navegacionAutomaticaPorModulos();
      // this.router.navigateByUrl('main');
    } catch (error: any) {
      // La API responde 400 con el mismo mensaje para usuario inexistente y para
      // clave incorrecta (no delata qué usuarios existen). Antes se comparaba
      // contra dos mensajes que ya no existen, y el error caía en el genérico.
      if (error?.status === 400) {
        this.helper.notifError('Usuario o contraseña incorrectos');
      } else {
        console.error(error);
        this.helper.notifError(error);
      }
    }
    this.loading.setLoading(false);
  }

  ngOnInit(): void {
    this.buildForm();
  }

  ngAfterViewInit(): void {
    if (this.googleHabilitado) this.initGoogle();
  }

  // Inicializa Google Identity Services y renderiza el botón. Reintenta hasta que
  // el script (gsi/client) y el contenedor estén disponibles.
  private initGoogle(retries = 20): void {
    const target = this.googleBtn?.nativeElement;
    const gis = (window as any).google?.accounts?.id;
    if (!gis || !target) {
      if (retries > 0) setTimeout(() => this.initGoogle(retries - 1), 150);
      return;
    }
    gis.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (res: { credential: string }) =>
        this.handleGoogleCredential(res.credential),
      ux_mode: 'popup',
      cancel_on_tap_outside: true,
    });
    gis.renderButton(target, {
      type: 'standard',
      // Botón de Google según el tema: el blanco quedaba como un parche en el
      // modo oscuro. Sólo los temas oficiales de Google.
      theme: this.helper.darkTheme ? 'filled_black' : 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: 320,
    });
  }

  private async handleGoogleCredential(idToken: string): Promise<void> {
    this.loading.setLoading(true);
    try {
      await this.login.loginGoogle(idToken);
      this.helper.navegacionAutomaticaPorModulos();
    } catch (error: any) {
      console.error(error);
      this.helper.notifError(
        error?.error?.message || 'No se pudo iniciar sesión con Google',
      );
    }
    this.loading.setLoading(false);
  }
}
